<template>
	<section class="member-order-detail">
		<div class="member-order-detail__wrap">
			<div class="member-order-detail__header">
				<el-page-header @back="handleBack" content="订单详情" />
				<el-tag type="info" effect="plain">
					单号：{{ soShortNr }}
				</el-tag>
			</div>

			<el-skeleton v-if="loading" rows="6" animated />

			<el-empty
				v-else-if="error"
				:image-size="120"
				:description="error"
			/>

			<div v-else-if="detail" class="member-order-detail__body">
				<el-card class="detail-card" shadow="never">
				<template #header>
					<div class="detail-card__header">
						<div>
							<h3>{{ detail.displayName || detail.orderNo }}</h3>
							<p>下单时间：{{ formatDate(detail.meta.orderDate) }}</p>
						</div>
						<el-tag size="medium" :type="statusType(detail.status)">
							{{ detail.status || '未知状态' }}
						</el-tag>
					</div>
				</template>
				<el-row :gutter="16">
					<el-col :md="8" :sm="24">
						<div class="summary-block">
							<label>预订员</label>
							<strong>{{ detail.booker.name || '-' }}</strong>
							<p>
								{{ detail.booker.company }}
								<span v-if="detail.booker.department">
									· {{ detail.booker.department }}
								</span>
							</p>
						</div>
					</el-col>
					<el-col :md="8" :sm="24">
						<div class="summary-block">
							<label>联系人</label>
							<strong>{{ detail.contact.name || '-' }}</strong>
							<p>{{ detail.contact.mobile || '-' }}</p>
						</div>
					</el-col>
					<el-col :md="8" :sm="24">
						<div class="summary-block">
							<label>订单金额</label>
							<strong class="price">￥{{ formatAmount(detail.pricing.totalAmount) }}</strong>
							<p>现金券：￥{{ formatAmount(detail.pricing.cashCoupon) }}</p>
						</div>
					</el-col>
				</el-row>
			</el-card>

			<el-card
				v-if="detail.flightTrips && detail.flightTrips.length"
				class="detail-card"
				shadow="never"
			>
				<template #header>
					<div class="detail-card__header">
						<h4>行程信息</h4>
					</div>
				</template>
				<el-timeline>
					<el-timeline-item
						v-for="(segment, index) in detail.flightTrips"
						:key="index"
						:timestamp="formatDate(segment.departureTime)"
						:type="statusType(segment.status)"
					>
						<div class="segment">
							<!-- 主要路线信息 -->
							<div class="segment__header">
								<div class="segment__route">
									<strong class="route-title">{{ segment.departureCity }} → {{ segment.arrivalCity }}</strong>
									<el-tag v-if="segment.status" size="mini" :type="statusType(segment.status)" style="margin-left: 8px;">
										{{ segment.status }}
									</el-tag>
								</div>
								<div class="segment__flight-info">
									<span v-if="segment.airlineName" class="airline-name">{{ segment.airlineName }}</span>
									<span class="flight-no">
										{{ formatFlightNo(segment) }}
									</span>
								</div>
							</div>

							<!-- 详细时间信息 -->
							<div class="segment__time-info">
								<div class="time-item">
									<div class="time-label">出发</div>
									<div class="time-value">
										<div class="time-main">{{ formatDateTime(segment.departureTime) }}</div>
										<div class="time-sub">
											{{ segment.departureAirport }}
											<span v-if="segment.departureAirportCode" class="airport-code">({{ segment.departureAirportCode }})</span>
										</div>
									</div>
								</div>
								<div class="time-arrow">
									<i class="el-icon-right"></i>
									<span v-if="segment.duration" class="duration">{{ segment.duration }}</span>
								</div>
								<div class="time-item">
									<div class="time-label">到达</div>
									<div class="time-value">
										<div class="time-main">{{ formatDateTime(segment.arrivalTime) }}</div>
										<div class="time-sub">
											{{ segment.arrivalAirport }}
											<span v-if="segment.arrivalAirportCode" class="airport-code">({{ segment.arrivalAirportCode }})</span>
										</div>
									</div>
								</div>
							</div>

							<!-- 其他详细信息 -->
							<div class="segment__details">
								<el-row :gutter="16">
									<el-col :span="12" v-if="segment.cabin">
										<div class="detail-item">
											<span class="detail-label">舱位等级：</span>
											<span class="detail-value">{{ segment.cabin }}</span>
										</div>
									</el-col>
									<el-col :span="12" v-if="segment.equipment">
										<div class="detail-item">
											<span class="detail-label">机型：</span>
											<span class="detail-value">{{ segment.equipment }}</span>
										</div>
									</el-col>
									<el-col :span="12" v-if="segment.seat">
										<div class="detail-item">
											<span class="detail-label">座位号：</span>
											<span class="detail-value">{{ segment.seat }}</span>
										</div>
									</el-col>
									<el-col :span="12" v-if="segment.meal">
										<div class="detail-item">
											<span class="detail-label">餐食：</span>
											<span class="detail-value">{{ segment.meal }}</span>
										</div>
									</el-col>
									<el-col :span="12" v-if="segment.baggage">
										<div class="detail-item">
											<span class="detail-label">行李额度：</span>
											<span class="detail-value">{{ segment.baggage }}</span>
										</div>
									</el-col>
									<el-col :span="12" v-if="segment.pnr">
										<div class="detail-item">
											<span class="detail-label">PNR：</span>
											<span class="detail-value">{{ segment.pnr }}</span>
										</div>
									</el-col>
									<el-col :span="12" v-if="segment.ticketNo">
										<div class="detail-item">
											<span class="detail-label">票号：</span>
											<span class="detail-value">{{ segment.ticketNo }}</span>
										</div>
									</el-col>
									<el-col :span="12" v-if="segment.flightDistance">
										<div class="detail-item">
											<span class="detail-label">飞行距离：</span>
											<span class="detail-value">{{ segment.flightDistance }}</span>
										</div>
									</el-col>
								</el-row>
							</div>

							<!-- 提示信息 -->
							<p class="segment__tips" v-if="segment.tips">
								<i class="el-icon-info"></i> {{ segment.tips }}
							</p>
						</div>
					</el-timeline-item>
				</el-timeline>
			</el-card>

			<el-card
				v-if="detail.hotelRooms && detail.hotelRooms.length"
				class="detail-card"
				shadow="never"
			>
				<template #header>
					<div class="detail-card__header">
						<h4>酒店信息</h4>
					</div>
				</template>
				<el-table :data="detail.hotelRooms" size="small" border>
					<el-table-column prop="hotelName" label="酒店名称" min-width="250">
						<template slot-scope="{ row }">
							<div v-if="row.hotelName" style="font-weight: 500;">{{ row.hotelName }}</div>
							<span v-else style="color: #909399;">-</span>
						</template>
					</el-table-column>
					<el-table-column prop="hotelAddress" label="地址" min-width="300">
						<template slot-scope="{ row }">
							<div v-if="row.hotelAddress">{{ row.hotelAddress }}</div>
							<span v-else style="color: #909399;">-</span>
						</template>
					</el-table-column>
					<el-table-column prop="hotelPhone" label="电话" min-width="180">
						<template slot-scope="{ row }">
							<div v-if="row.hotelPhone">
								<i class="el-icon-phone" style="margin-right: 5px;"></i>
								{{ row.hotelPhone }}
							</div>
							<span v-else style="color: #909399;">-</span>
						</template>
					</el-table-column>
				</el-table>
			</el-card>

			<el-card
				v-if="detail.trainTrips && detail.trainTrips.length"
				class="detail-card"
				shadow="never"
			>
				<template #header>
					<div class="detail-card__header">
						<h4>车票信息</h4>
					</div>
				</template>
				<el-table :data="detail.trainTrips" size="small" border>
					<el-table-column prop="route" label="行程" min-width="200" />
					<el-table-column prop="trainNo" label="车次" width="120" />
					<el-table-column prop="seatType" label="席别" width="120" />
					<el-table-column label="出发" min-width="200">
						<template slot-scope="{ row }">
							<div>{{ row.departureStation }}</div>
							<div class="text-muted">{{ formatDate(row.departureTime) }}</div>
						</template>
					</el-table-column>
					<el-table-column label="到达" min-width="200">
						<template slot-scope="{ row }">
							<div>{{ row.arrivalStation }}</div>
							<div class="text-muted">{{ formatDate(row.arrivalTime) }}</div>
						</template>
					</el-table-column>
					<el-table-column prop="status" label="状态" width="120" />
				</el-table>
			</el-card>

			<el-card class="detail-card" shadow="never">
				<template #header>
					<div class="detail-card__header">
						<h4>乘机人 / 旅客</h4>
					</div>
				</template>
				<el-table :data="detail.passengers" size="small" border>
					<el-table-column prop="type" label="类型" width="100">
						<template slot-scope="{ row }">
							{{ row.type || '-' }}
						</template>
					</el-table-column>
					<el-table-column prop="name" label="姓名" width="150" />
					<el-table-column prop="certType" label="证件类型" width="120">
						<template slot-scope="{ row }">
							{{ formatCertType(row.certType) }}
						</template>
					</el-table-column>
					<el-table-column prop="certNr" label="证件号" min-width="180">
						<template slot-scope="{ row }">
							{{ row.certNr || '-' }}
						</template>
					</el-table-column>
					<el-table-column prop="phone" label="联系电话" width="160" />
				</el-table>
			</el-card>

			<el-card class="detail-card" shadow="never">
				<template #header>
					<div class="detail-card__header">
						<h4>费用明细</h4>
					</div>
				</template>
				<el-table :data="detail.fees" size="small" border>
					<el-table-column prop="category" label="产品" width="100">
						<template slot-scope="{ row }">
							{{ categoryLabel(row.category) }}
						</template>
					</el-table-column>
					<el-table-column prop="item" label="项目" min-width="140">
						<template slot-scope="{ row }">
							{{ row.item || '-' }}
						</template>
					</el-table-column>
					<el-table-column prop="price" label="金额" width="120" align="right">
						<template slot-scope="{ row }">
							{{ formatAmount(row.price) }}
						</template>
					</el-table-column>
					<el-table-column prop="tax" label="税费" width="120" align="right">
						<template slot-scope="{ row }">
							{{ formatAmount(row.tax) }}
						</template>
					</el-table-column>
					<el-table-column prop="otherFee" label="其他" width="120" align="right">
						<template slot-scope="{ row }">
							{{ formatAmount(row.otherFee) }}
						</template>
					</el-table-column>
					<el-table-column prop="total" label="合计" width="120" align="right">
						<template slot-scope="{ row }">
							<strong style="color: #f56c6c;">{{ formatAmount(row.total) }}</strong>
						</template>
					</el-table-column>
				</el-table>
			</el-card>
			
			<!-- 操作日志已隐藏，不展示给客户 -->
			<!-- <el-row :gutter="16">
				<el-col :md="12" :sm="24">
					<el-card class="detail-card" shadow="never">
						<template #header>
							<div class="detail-card__header">
								<h4>操作日志</h4>
							</div>
						</template>
						<el-timeline v-if="detail.messages.length">
							<el-timeline-item
								v-for="msg in detail.messages"
								:key="msg.ID"
								:timestamp="formatDate(msg.CreateDate)"
								type="info"
							>
								<div class="message">
									<strong>{{ msg.OperationID }}</strong>
									<p>{{ msg.Content }}</p>
								</div>
							</el-timeline-item>
						</el-timeline>
						<el-empty
							v-else
							image-size="80"
							description="暂无日志"
						/>
					</el-card>
				</el-col>
			</el-row> -->
			</div>
		</div>
	</section>
</template>

<script>
import { fetchOrderDetail } from './services/memberCenterService'

export default {
	name: 'MemberOrderDetail',
	data() {
		return {
			loading: true,
			error: '',
			detail: null,
			soShortNr: this.$route.params.soShortNr || this.$route.query.SOShortNr || ''
		}
	},
	watch: {
		'$route.params.soShortNr'(val) {
			if (val && val !== this.soShortNr) {
				this.soShortNr = val
				this.loadDetail()
			}
		}
	},
	created() {
		this.loadDetail()
	},
	methods: {
		async loadDetail() {
			if (!this.soShortNr) {
				this.error = '缺少订单号'
				this.loading = false
				return
			}
			this.loading = true
			this.error = ''
			try {
				const { detail } = await fetchOrderDetail(this.soShortNr)
				this.detail = detail
			} catch (err) {
				this.error = err?.message || '加载订单详情失败'
			} finally {
				this.loading = false
			}
		},
		handleBack() {
			if (window.history.length > 1) {
				this.$router.back()
			} else {
				this.$router.push({ name: 'MemberCenter', query: { section: 'orders' } })
			}
		},
		formatDate(value) {
			if (!value) {
				return '-'
			}
			return new Date(value).toLocaleString()
		},
		formatDateTime(value) {
			if (!value) {
				return '-'
			}
			const date = new Date(value)
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')
			return `${year}-${month}-${day} ${hours}:${minutes}`
		},
		formatAmount(amount) {
			if (!amount && amount !== 0) {
				return '0.00'
			}
			return Number(amount).toLocaleString('zh-CN', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			})
		},
		statusType(status) {
			const map = {
				已出票: 'success',
				退款完成: 'info',
				退订成功: 'info',
				已取消: 'danger',
				待支付: 'warning',
				已完成: 'success'
			}
			return map[status] || 'info'
		},
		categoryLabel(cat) {
			const map = {
				flight: '机票',
				hotel: '酒店',
				train: '车票'
			}
			return map[cat] || cat || '-'
		},
		formatCertType(certType) {
			if (!certType) return '-'
			// 证件类型映射
			const certTypeMap = {
				'ID': '身份证',
				'PASSPORT': '护照',
				'IDCard': '身份证',
				'Passport': '护照',
				'1': '身份证',
				'2': '护照',
				'3': '军官证',
				'4': '士兵证',
				'5': '回乡证',
				'6': '台胞证',
				'7': '国际海员证',
				'8': '外国人永久居留证',
				'9': '其他'
			}
			return certTypeMap[certType] || certType
		},
		formatRange(start, end) {
			if (!start && !end) {
				return '-'
			}
			return `${this.formatDate(start)} - ${this.formatDate(end)}`
		},
		formatFlightNo(segment) {
			if (!segment) return '-'
			// 航班号已经在数据映射时处理过了（如果是纯数字会自动加上航司代码）
			// 直接显示即可
			return segment.flightNo || '-'
		}
	}
}
</script>

<style lang="scss" scoped>
.member-order-detail {
	padding: 24px 32px 48px;
	background: #f5f6fa;
	min-height: 100vh;

	&__wrap {
		max-width: 1280px;
		margin: 0 auto;
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	&__body {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}
}

.detail-card {
	border-radius: 16px;

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;

		h3,
		h4 {
			margin: 0;
			font-weight: 600;
		}

		p {
			margin: 4px 0 0;
			color: #909399;
			font-size: 13px;
		}
	}
}

.summary-block {
	background: #f8f9fb;
	padding: 16px;
	border-radius: 12px;

	label {
		font-size: 12px;
		color: #909399;
	}

	strong {
		display: block;
		margin-top: 6px;
		font-size: 18px;
		color: #333;
	}

	p {
		margin: 4px 0 0;
		color: #909399;
		font-size: 13px;
	}
}

.price {
	color: #f56c6c;
}

.segment {
	padding: 16px;
	background: #fafbfc;
	border-radius: 8px;
	margin-bottom: 8px;

	&__header {
		margin-bottom: 16px;
		padding-bottom: 12px;
		border-bottom: 1px solid #e4e7ed;
	}

	&__route {
		display: flex;
		align-items: center;
		margin-bottom: 8px;

		.route-title {
			font-size: 18px;
			color: #303133;
			font-weight: 600;
		}
	}

	&__flight-info {
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 14px;
		margin-top: 8px;
		padding: 8px 12px;
		background: #f0f9ff;
		border-radius: 6px;
		border: 1px solid #b3d8ff;

		.airline-name {
			color: #303133;
			font-weight: 600;
			font-size: 15px;
		}

		.flight-no {
			font-weight: 700;
			color: #409eff;
			font-size: 18px;
			letter-spacing: 1px;
			
			.airline-code {
				font-weight: 700;
				color: #409eff;
				margin-right: 2px;
			}
		}
	}

	&__time-info {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin: 16px 0;
		padding: 16px;
		background: #fff;
		border-radius: 8px;
		border: 1px solid #e4e7ed;

		.time-item {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 8px;

			.time-label {
				font-size: 12px;
				color: #909399;
			}

			.time-value {
				.time-main {
					font-size: 16px;
					font-weight: 600;
					color: #303133;
					margin-bottom: 4px;
				}

				.time-sub {
					font-size: 13px;
					color: #606266;

					.airport-code {
						color: #909399;
						margin-left: 4px;
					}
				}
			}
		}

		.time-arrow {
			display: flex;
			flex-direction: column;
			align-items: center;
			margin: 0 16px;
			color: #909399;

			.duration {
				font-size: 12px;
				margin-top: 4px;
				color: #606266;
			}
		}
	}

	&__details {
		margin: 16px 0;
		padding: 12px;
		background: #fff;
		border-radius: 6px;

		.detail-item {
			margin-bottom: 8px;
			font-size: 13px;

			.detail-label {
				color: #909399;
			}

			.detail-value {
				color: #303133;
				font-weight: 500;
			}
		}
	}

	&__tips {
		margin: 12px 0 0;
		padding: 8px 12px;
		background: #ecf5ff;
		border-left: 3px solid #409eff;
		border-radius: 4px;
		font-size: 12px;
		color: #606266;
		white-space: pre-line;

		i {
			margin-right: 4px;
			color: #409eff;
		}
	}
}

.message {
	strong {
		display: block;
		margin-bottom: 4px;
	}

	p {
		margin: 0;
	}
}

.text-muted {
	color: #909399;
	font-size: 12px;
}

@media (max-width: 768px) {
	.member-order-detail {
		padding: 16px;
	}
}
</style>

